import Head from 'next/head';
import { generateNextSeo } from 'next-seo/pages';

/* Constants */
import { PAGE_TYPE_COOKIES } from '@constants/pages';

/* Components */
import { getLayout } from '@components/PageLayouts/Layout/Standard';
import { Grid } from 'ht-styleguide';

/* Types */
import { Children } from 'types/base.types';

import styles from '@scenes/TosPage/tosPage.module.scss';
import cn from 'classnames';

function CookiesPage(): Children {
  return (
    <>
      <Head>
        {generateNextSeo({
          title: 'Cookies Policy',
          description: 'Cookies policy for HelloTech, the leading home services & tech support service provider.',
        })}
      </Head>
      <div className={cn('paddingY-huge', styles.tosHero)}>
        <Grid.Fluid>
          <h1 className="h2 marginBottom-small white textAlignCenter">HelloTech Cookies Policy</h1>
          <p className="white textAlignCenter">Last Modified on December 05, 2025</p>
        </Grid.Fluid>
      </div>
      <Grid.Fluid classes="paddingY-large">
        <div className={styles.tosContainer}>
          <section>
            <p>
              <a onClick={() => window?.Osano.cm.showDrawer('osano-cm-dom-info-dialog-open')} className="text-button" href="#">
                Manage Cookie Preferences
              </a>
              <br />
              <br />
              At HelloTech, we are committed to ensuring that your interaction with our website is smooth, personalized, and efficient. Our use of
              cookies is designed with this commitment in mind. This Cookie Policy provides detailed information on our use of cookies, including the
              types we use and the purposes they serve.
            </p>
          </section>

          <section>
            <br />
            <br />
            <p>
              <strong className="h4">What Are Cookies?</strong>
            </p>
            <p>
              Cookies are small text files stored on your device (computer, tablet, smartphone, etc.) when you visit a website. These files are used
              by the website to recognize your device on subsequent visits, enhancing your user experience by remembering your preferences and usage
              patterns. For more information about cookies, we recommend visiting{' '}
              <a href="https://www.allaboutcookies.org" rel="noreferrer" target="_blank">
                www.allaboutcookies.org
              </a>{' '}
              and{' '}
              <a href="https://www.youronlinechoices.eu" rel="noreferrer" target="_blank">
                www.youronlinechoices.eu
              </a>
              .
            </p>
          </section>

          <section>
            <br />
            <br />
            <p>
              <strong className="h4">How We Use Cookies</strong>
            </p>
            <p>
              HelloTech utilizes cookies for various purposes to improve your experience on our site. The cookies we use can be divided into the
              following categories:
            </p>

            <p className="h5">
              <strong>1. Essential Cookies</strong>
            </p>
            <p>
              These cookies are strictly necessary for our website to function and cannot be switched off without affecting the use of the site. They
              are set in response to actions made by you, such as setting your privacy preferences or logging in.
            </p>

            <p className="h5">
              <strong>2. Analytical Cookies</strong>
            </p>
            <p>
              We use analytical cookies to understand how our visitors interact with the website. This includes tracking pages visited and actions
              taken. The tools we use for this analysis include Segment, Google Analytics, Fullstory, and Split. These cookies help us improve our
              site's functionality and your overall experience.
            </p>

            <p className="h5">
              <strong>3. Functional Cookies</strong>
            </p>
            <p>
              Functional cookies enable our website to provide enhanced functionality and personalization, based on your interactions. We use Gladly
              for chat functionalities, ensuring seamless communication with our support team, and Wisepops for deploying marketing promotions that
              are relevant to your interests.
            </p>
            <p className="h5">
              <strong>4. Advertising Cookies</strong>
            </p>
            <p>
              HelloTech utilizes advertising cookies to deliver ads that are most relevant to you and your interests. These cookies may track your
              online activity and collect information to deliver tailored advertising across various platforms, including Google Ads, Bing Ads, Impact
              Radius, Facebook, and Instagram.
            </p>
          </section>
          <section>
            <br />
            <p className="h4">E.U. Users' Right to Refuse Cookies</p>
            <p>
              If you are in the European Union, you have the right to refuse the placement of cookies on your device. You can exercise this right by
              adjusting your browser settings to decline cookies. Please note, however, that doing so may impact your ability to use certain features
              of our website effectively.
            </p>
          </section>

          <section>
            <br />
            <p>
              <strong className="h4">Managing Cookies</strong>
            </p>
            <p>
              Most web browsers allow some control of most cookies through the browser settings. To find out more about how to manage and delete
              cookies, visit www.allaboutcookies.org.
            </p>
          </section>
          <section>
            <br />
            <p>
              <strong className="h4">Have Questions?</strong>
            </p>
            <p>
              If you have any questions or concerns regarding our Cookie Policy, please do not hesitate to contact us at{' '}
              <a href="mailto:privacy@hellotech.com">privacy@hellotech.com</a>.
            </p>
          </section>
          <section>
            <br />
            <p>
              <strong className="h4">Policy Updates</strong>
            </p>
            <p>
              We may update this Cookie Policy from time to time to reflect changes in our practices or for other operational, legal, or regulatory
              reasons. We encourage you to review our Cookie Policy periodically to stay informed about how we are using cookies.
            </p>
          </section>
        </div>
      </Grid.Fluid>
    </>
  );
}

CookiesPage.pageAttributes = { page: PAGE_TYPE_COOKIES };
CookiesPage.Layout = getLayout({ promo: false, footer: true, partnerEnabled: false });

export default CookiesPage;
